import { createBrowserClient } from '@supabase/ssr';

// ── Singleton browser client ─────────────────────────────────────────────────
// CRITICAL: One instance only. Multiple instances each run their own token
// refresh machinery, causing lock conflicts and "lock stolen" errors.
// NEVER reset _client to null — doing so causes a new instance to be created
// on the next call, which starts a second refresh cycle.
let _client: ReturnType<typeof createBrowserClient> | null = null;

export function createClient() {
  if (_client) return _client;

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
  const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

  _client = createBrowserClient(supabaseUrl, supabaseKey);

  return _client;
}

/**
 * Hard-clear all Supabase auth tokens from cookies and localStorage.
 * Does NOT reset the singleton — the same client instance is reused
 * so no second refresh cycle is spawned.
 */
export function clearAuthStorage(): void {
  if (typeof window === 'undefined') return;

  // Clear from localStorage
  try {
    const keysToRemove = Object.keys(localStorage).filter(
      (k) => k.startsWith('sb-') || k.includes('supabase')
    );
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {}

  // Clear from cookies
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('sb-') || name.includes('supabase')) {
        document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
      }
    });
  } catch {}
}
